import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { financialRecordsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { fmt } from "./reportUtils";

interface MaintenanceReportsProps {
  period: string;
}

interface MaintenanceItem {
  id: string;
  title: string;
  description: string;
  area: string;
  status: "planned" | "in_progress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "urgent";
  cost: number;
  startDate: string;
  completionDate?: string;
  supplier: string;
}

const MaintenanceReports: React.FC<MaintenanceReportsProps> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maintenanceData, setMaintenanceData] = useState<MaintenanceItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [completedItems, setCompletedItems] = useState(0);
  const [inProgressItems, setInProgressItems] = useState(0);

  useEffect(() => {
    const fetchMaintenanceData = async () => {
      if (!currentBuilding?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const records = await financialRecordsApi.getAll(currentBuilding.id);
        const bills = records.filter((r: any) => r.type === "bill");
        const maintenanceItems = bills
          .filter((e: any) => e.category === "maintenance")
          .map((e: any) => ({
            id: e.id,
            title: e.title || e.description,
            description: e.description,
            area: e.area || "General",
            status:
              e.status === "approved"
                ? "completed"
                : e.status === "pending"
                ? "planned"
                : e.status || "planned",
            priority: e.priority || "medium",
            cost: e.amount,
            startDate: e.date,
            completionDate: e.completionDate,
            supplier: e.vendor || e.submittedBy || "Unknown",
          }));
        setMaintenanceData(maintenanceItems);
        setTotalItems(maintenanceItems.length);
        const total = maintenanceItems.reduce(
          (sum: number, item: any) => sum + item.cost,
          0,
        );
        setTotalCost(total);
        const completed = maintenanceItems.filter(
          (item: any) => item.status === "completed",
        ).length;
        setCompletedItems(completed);
        const inProgress = maintenanceItems.filter(
          (item: any) => item.status === "in_progress",
        ).length;
        setInProgressItems(inProgress);
      } catch (err) {
        console.error("Error fetching maintenance report data:", err);
        setError("Failed to load maintenance reports. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchMaintenanceData();
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading maintenance reports..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Maintenance Reports
      </h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Total Maintenance Items
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {totalItems}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Total Cost
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {fmt(totalCost)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Completed
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-green-600">
              {completedItems}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              In Progress
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-yellow-600">
              {inProgressItems}
            </dd>
          </div>
        </div>
      </div>

      {/* Maintenance Items Table */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Maintenance Items
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Title
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Area
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Priority
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Cost
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Dates
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {maintenanceData.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.area}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          item.priority === "urgent"
                            ? "bg-red-100 text-red-800"
                            : item.priority === "high"
                            ? "bg-orange-100 text-orange-800"
                            : item.priority === "medium"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {item.priority.charAt(0).toUpperCase() +
                          item.priority.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          item.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : item.status === "in_progress"
                            ? "bg-primary-100 text-primary-800"
                            : item.status === "planned"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {item.status.replace("_", " ").charAt(0).toUpperCase() +
                          item.status.replace("_", " ").slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {fmt(item.cost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        Start: {new Date(item.startDate).toLocaleDateString()}
                      </div>
                      {item.completionDate && (
                        <div>
                          End:{" "}
                          {new Date(item.completionDate).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Charts and Visualizations */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Maintenance by Area
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {/* Simplified pie chart for maintenance by area */}
            <div className="flex flex-col items-center">
              <div className="w-48 h-48 rounded-full border-8 border-gray-200 relative">
                {/* This would ideally be a real chart with proper calculations */}
                <div
                  className="absolute bg-primary-500 rounded-tl-full"
                  style={{ width: "50%", height: "50%", top: 0, left: 0 }}
                ></div>
                <div
                  className="absolute bg-green-500 rounded-tr-full"
                  style={{ width: "50%", height: "50%", top: 0, right: 0 }}
                ></div>
                <div
                  className="absolute bg-yellow-500 rounded-bl-full"
                  style={{ width: "50%", height: "50%", bottom: 0, left: 0 }}
                ></div>
                <div
                  className="absolute bg-red-500 rounded-br-full"
                  style={{ width: "50%", height: "50%", bottom: 0, right: 0 }}
                ></div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-primary-500 mr-2"></div>
                  <span className="text-xs text-gray-600">Exterior: 40%</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 mr-2"></div>
                  <span className="text-xs text-gray-600">Interior: 20%</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 mr-2"></div>
                  <span className="text-xs text-gray-600">Services: 25%</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 mr-2"></div>
                  <span className="text-xs text-gray-600">Safety: 15%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Maintenance Cost by Month
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {/* Simplified bar chart for maintenance costs */}
            <div className="h-64">
              <div className="flex h-48 items-end space-x-2">
                <div
                  className="flex-1 bg-primary-500"
                  style={{ height: "20%" }}
                ></div>
                <div
                  className="flex-1 bg-primary-500"
                  style={{ height: "80%" }}
                ></div>
                <div
                  className="flex-1 bg-primary-500"
                  style={{ height: "40%" }}
                ></div>
                <div
                  className="flex-1 bg-primary-500"
                  style={{ height: "60%" }}
                ></div>
                <div
                  className="flex-1 bg-primary-500"
                  style={{ height: "30%" }}
                ></div>
                <div
                  className="flex-1 bg-primary-500"
                  style={{ height: "10%" }}
                ></div>
              </div>
              <div className="mt-2 flex justify-between text-xs text-gray-600">
                <span>Jan</span>
                <span>Feb</span>
                <span>Mar</span>
                <span>Apr</span>
                <span>May</span>
                <span>Jun</span>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2 text-center">
              Showing maintenance costs for first half of{" "}
              {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceReports;
